import { toast } from "react-toastify";

/**
 * Utilitários para exclusão singular ou em lote a partir do componente Table.
 * 
*/

export type DeleteResult = {
  id: string | number;
  status: number;
  data?: any;
};

export type EntityLabels = {
  singular: string;
  plural: string;
  gender?: "f" | "m";
};

/**
 * Trecho presente nas duas variantes da mensagem de violação de chave
 * estrangeira geradas pelo backend (postgresErrorMap/postgresErrorHandler).
 * Concentrado aqui para que uma mudança de texto no backend tenha um único
 * ponto de ajuste no front.
 */
const FOREIGN_KEY_MESSAGE = "está sendo utilizado";

/**
 * Executa as exclusões em lotes, para não disparar uma requisição por item de
 * uma só vez. 
 * Necessário porque estas entidades só expõem `DELETE /rota/:id` no backend —
 * quando houver endpoint de exclusão em lote, uma única chamada substitui isto.
 */
export const runDeleteBatch = async (
  ids: (string | number)[],
  deleteOne: (id: string | number) => Promise<{ status: number; data?: any }>,
  chunkSize = 5,
): Promise<DeleteResult[]> => {
  const results: DeleteResult[] = [];

  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    const settled = await Promise.all(
      chunk.map(async (id) => ({ id, ...(await deleteOne(id)) })),
    );
    results.push(...settled);
  }

  return results;
};

export const isDeleteSuccess = (result: DeleteResult) =>
  result.status >= 200 && result.status < 300;

export const isBlockedByRelation = (result: DeleteResult) => {
  const message = result.data?.message;
  return typeof message === "string" && message.includes(FOREIGN_KEY_MESSAGE);
};

/** Lista os ids nas mensagens, sem gerar toast quilométrico. */
export const listIds = (items: DeleteResult[], maxItems = 5) => {
  const labels = items.map((item) => `#${item.id}`);
  return labels.length <= maxItems
    ? labels.join(", ")
    : `${labels.slice(0, maxItems).join(", ")} e mais ${labels.length - maxItems}`;
};

/**
 * Emite os toasts de uma exclusão em lote: um de sucesso com a contagem, um
 * para os bloqueios por vínculo e um para as demais falhas. Cada mensagem cita
 * os ids envolvidos.
 */
export const notifyDeleteResults = (
  results: DeleteResult[],
  { singular, plural, gender = "m" }: EntityLabels,
) => {
  const suffix = gender === "f" ? "a" : "o";

  const succeeded = results.filter(isDeleteSuccess);
  const failed = results.filter((result) => !isDeleteSuccess(result));
  const blockedByRelation = failed.filter(isBlockedByRelation);
  const otherFailures = failed.filter((result) => !isBlockedByRelation(result));

  if (succeeded.length > 0) {
    toast.success(
      succeeded.length > 1
        ? `${succeeded.length} ${plural} excluíd${suffix}s com sucesso: ${listIds(succeeded)}.`
        : `${singular} ${listIds(succeeded)} excluíd${suffix} com sucesso!`,
    );
  }

  if (blockedByRelation.length > 0) {
    toast.error(
      `Não é possível excluir ${plural} que já possuem alguma relação: ` +
        `${listIds(blockedByRelation)}.`,
    );
  }

  if (otherFailures.length > 0) {
    toast.error(`Não foi possível excluir: ${listIds(otherFailures)}.`);
  }
};
